import React from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import { ErrorPanel, Progress } from '@backstage/core-components';

export const floatingPanelStyles = {
  position: 'fixed' as const,
  bottom: 16,
  right: 16,
  zIndex: 9999,
  maxWidth: 520,
};

export interface BackendHealthIndicatorProps {
  showPollProgress: boolean;
  panelTitle: string;
  panelError: Error;
  /** Neutral “still checking” UI; default is {@link ErrorPanel} for failures and warnings. */
  panelVariant?: 'checking' | 'error';
}

/**
 * Presentational backend **readiness** floating panel. Pass props from
 * {@link useBackendHealthIndicatorData} or the connected export in
 * `BackendHealthStatusContext` (local dev). This UI is only about readiness, not
 * browser `navigator.onLine`; combined browser/backend offline framing is
 * `OfflineIndicator` (fixed viewport ring above the app).
 */
export const BackendHealthIndicator: React.FC<BackendHealthIndicatorProps> = ({
  showPollProgress,
  panelTitle,
  panelError,
  panelVariant = 'error',
}) => (
  <Box
    sx={floatingPanelStyles}
    data-testid="backend-health-indicator"
    role={panelVariant === 'checking' ? 'status' : undefined}
    aria-live={panelVariant === 'checking' ? 'polite' : undefined}
  >
    {panelVariant === 'checking' ? (
      <Paper elevation={6} style={{ padding: 16 }}>
        <Typography variant="subtitle1" component="h2" gutterBottom>
          {panelTitle}
        </Typography>
        <Box sx={{ my: 1 }} data-testid="backend-health-indicator-poll-progress">
          <Progress />
        </Box>
        <Typography variant="body2" color="textSecondary">
          {panelError.message}
        </Typography>
      </Paper>
    ) : (
      <>
        {showPollProgress && (
          <Box sx={{ mb: 1 }} data-testid="backend-health-indicator-poll-progress">
            <Progress />
          </Box>
        )}
        <ErrorPanel error={panelError} title={panelTitle} />
      </>
    )}
  </Box>
);
